import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import './ImageEditor.css';

interface ImageEditorProps {
  src: string;
  onSave: (croppedImage: string, width: number, height: number) => void;
  onCancel: () => void;
  initialWidth?: number;
  initialHeight?: number;
  targetWidth?: number;
  targetHeight?: number;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export const ImageEditor: React.FC<ImageEditorProps> = ({
  src,
  onSave,
  onCancel,
  initialWidth = 300,
  initialHeight = 200,
}) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState<number | undefined>(16 / 9);
  const [isFreeAspect, setIsFreeAspect] = useState(false);
  const [imageWidth, setImageWidth] = useState(initialWidth);
  const [imageHeight, setImageHeight] = useState(initialHeight);

  const imgRef = useRef<HTMLImageElement>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }, [aspect]);

  const getCroppedImg = useCallback(
    async (imageSrc: string, crop: PixelCrop): Promise<{ dataUrl: string; width: number; height: number }> => {
      if (!imgRef.current) {
        throw new Error('Image ref not available');
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      const displayedImg = imgRef.current;
      const scaleX = displayedImg.naturalWidth / displayedImg.width;
      const scaleY = displayedImg.naturalHeight / displayedImg.height;

      // Use the desired output dimensions from sliders
      canvas.width = imageWidth;
      canvas.height = imageHeight;

      try {
        // Fetch image as blob to avoid CORS issues
        const response = await fetch(imageSrc);
        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);
        
        // Load image from blob URL (not tainted)
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = objectURL;
        });

        // Draw the cropped portion
        ctx.drawImage(
          img,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          imageWidth,
          imageHeight,
        );

        // Clean up
        URL.revokeObjectURL(objectURL);

        // Try to export
        try {
          return {
            dataUrl: canvas.toDataURL('image/jpeg'),
            width: imageWidth,
            height: imageHeight
          };
        } catch {
          // Fallback to PNG
          return {
            dataUrl: canvas.toDataURL('image/png'),
            width: imageWidth,
            height: imageHeight
          };
        }
      } catch (error) {
        console.error('Failed to fetch and crop image:', error);
        
        // Last resort: try with the displayed image element
        try {
          ctx.drawImage(
            displayedImg,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            imageWidth,
            imageHeight,
          );

          return {
            dataUrl: canvas.toDataURL('image/png'),
            width: imageWidth,
            height: imageHeight
          };
        } catch (canvasError) {
          console.error('Canvas export also failed:', canvasError);
          // Return original image as absolute last resort
          return {
            dataUrl: imageSrc,
            width: imageWidth,
            height: imageHeight
          };
        }
      }
    },
    [imageWidth, imageHeight],
  );

  const handleSave = async () => {
    if (completedCrop && imgRef.current) {
      const result = await getCroppedImg(src, completedCrop);
      // Use the actual output dimensions from the canvas
      onSave(result.dataUrl, result.width, result.height);
    }
  };

  const handleAspectChange = (newAspect: number | undefined) => {
    setAspect(newAspect);
    setIsFreeAspect(!newAspect);
    if (newAspect && imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(centerAspectCrop(width, height, newAspect));
    }
  };

  const modalContent = (
    <div 
      className="absolute inset-0 flex items-center justify-center p-4"
      style={{ 
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.8)'
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-editor-title"
    >
      <div 
        className="rounded-lg p-6 max-w-6xl w-full max-h-[95vh] overflow-auto"
        style={{
          background: 'linear-gradient(to bottom, #1e293b, #0f172a)',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          color: 'white'
        }}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 id="image-editor-title" className="text-xl font-semibold text-white">Edit Image</h3>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-800 hover:border-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Image Editor */}
          <div className="lg:col-span-2">
            <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={isFreeAspect ? undefined : aspect}
                minWidth={50}
                minHeight={50}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={src}
                  // crossOrigin="anonymous"
                  style={{
                    transform: `scale(${scale}) rotate(${rotate}deg)`,
                    maxWidth: '100%',
                    maxHeight: '70vh',
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-3">
                Aspect Ratio
              </label>
              <div className="space-y-2">
                <button
                  onClick={() => handleAspectChange(16 / 9)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg transition-colors ${
                    aspect === 16 / 9 
                      ? 'bg-blue-600 border-blue-500 text-white' 
                      : 'border-gray-600 text-gray-300 hover:bg-gray-800 hover:border-gray-500'
                  }`}
                >
                  16:9 (Landscape)
                </button>
                <button
                  onClick={() => handleAspectChange(4 / 3)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg transition-colors ${
                    aspect === 4 / 3 
                      ? 'bg-blue-600 border-blue-500 text-white' 
                      : 'border-gray-600 text-gray-300 hover:bg-gray-800 hover:border-gray-500'
                  }`}
                >
                  4:3 (Standard)
                </button>
                <button
                  onClick={() => handleAspectChange(1)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg transition-colors ${
                    aspect === 1 
                      ? 'bg-blue-600 border-blue-500 text-white' 
                      : 'border-gray-600 text-gray-300 hover:bg-gray-800 hover:border-gray-500'
                  }`}
                >
                  1:1 (Square)
                </button>
                <button
                  onClick={() => handleAspectChange(3 / 4)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg transition-colors ${
                    aspect === 3 / 4 
                      ? 'bg-blue-600 border-blue-500 text-white' 
                      : 'border-gray-600 text-gray-300 hover:bg-gray-800 hover:border-gray-500'
                  }`}
                >
                  3:4 (Portrait)
                </button>
                <button
                  onClick={() => handleAspectChange(undefined)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg transition-colors ${
                    isFreeAspect 
                      ? 'bg-blue-600 border-blue-500 text-white' 
                      : 'border-gray-600 text-gray-300 hover:bg-gray-800 hover:border-gray-500'
                  }`}
                >
                  Free Form
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Scale: {scale.toFixed(2)}x
              </label>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Rotate: {rotate}°
              </label>
              <input
                type="range"
                min="-180"
                max="180"
                step="1"
                value={rotate}
                onChange={(e) => setRotate(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Output Width: {imageWidth}px
              </label>
              <input
                type="range"
                min="100"
                max="800"
                step="10"
                value={imageWidth}
                onChange={(e) => setImageWidth(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Output Height: {imageHeight}px
              </label>
              <input
                type="range"
                min="100"
                max="600"
                step="10"
                value={imageHeight}
                onChange={(e) => setImageHeight(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Find the main content container, fallback to body if not found
  const portalTarget = document.querySelector('.main-content') || 
                      document.querySelector('.step-content-container') || 
                      document.querySelector('.editor-layout') || 
                      document.body;
  
  return createPortal(modalContent, portalTarget);
};
